import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AdminEmailLogs from "../components/admin/AdminEmailLogs";
import AdminEmailSettings from "../components/admin/AdminEmailSettings";
import AdminProfile from "../components/admin/AdminProfile";
import AdminUsers from "../components/admin/AdminUsers";
import { LogOut, FileText, Mail, UserCircle, ExternalLink, Settings, Shield } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { DEVELOPER_TABS as tabs } from "../lib/tabs";

export default function DeveloperDashboard() {
  const [tab, setTab] = useState("logs");
  const { user, profile, logout, loading } = useAuth();
  const navigate = useNavigate();

  const role = (user?.role || "").trim().toLowerCase();
  const isDev = role === "developer" || role === "dev" || role === "programador";

  useEffect(() => {
    if (!loading && (!user || !isDev)) {
      navigate("/backoffice/login");
    }
  }, [user, loading, navigate, isDev]);

  if (loading || !user || !isDev) return null;

  function onLogout() {
    logout();
    navigate("/backoffice/login");
  }

  return (
    <div data-id="page-developer-dashboard" className="min-h-screen bg-offwhite">
      <header className="bg-white border-b border-fendi sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-10 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 outline-none hover:opacity-80 transition-opacity" data-id="dev-user-menu" data-testid="dev-user-menu">
                  <Avatar className="h-7 w-7 md:h-8 md:w-8 border border-fendi/20">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <AvatarFallback className="bg-champagne/10 text-softblack text-[9px] md:text-[10px] font-serif uppercase">
                        {profile?.display_name?.charAt(0) || user?.email?.charAt(0) || 'D'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 mt-1">
                <DropdownMenuLabel className="font-serif font-normal text-warmgray text-xs uppercase tracking-wider">
                  {profile?.display_name || user?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTab("profile")} className="cursor-pointer">
                  <UserCircle className="mr-2 h-4 w-4 text-warmgray" />
                  <span className="text-xs uppercase tracking-widest">Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span className="text-xs uppercase tracking-widest">Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/" className="font-serif text-lg md:text-2xl text-softblack" data-id="dev-logo" data-testid="dev-logo">
              Dev<span className="text-champagne">.</span>
            </Link>
            
            <nav className="hidden lg:flex gap-2 ml-4" data-id="dev-tabs" data-testid="dev-tabs">
              {tabs.map((t) => (
                <button
                  key={t.uid}
                  onClick={() => setTab(t.uid)}
                  data-id={`dev-tab-${t.uid}`} data-testid={`dev-tab-${t.uid}`}
                  className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors ${tab === t.uid ? "text-softblack border-b border-champagne" : "text-warmgray hover:text-softblack"}`}
                >
                  <t.Icon size={14} strokeWidth={1.5} />
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/" target="_blank" rel="noopener noreferrer" className="text-[10px] lg:text-xs text-warmgray hover:text-softblack" data-id="dev-view-site" data-testid="dev-view-site">
              <ExternalLink size={10} className="lg:hidden" />
              <span className="hidden lg:inline-flex items-center gap-1 eyebrow"><ExternalLink size={12} /> Ver site</span>
            </Link>
          </div>
        </div>
        <nav className="lg:hidden border-t border-fendi/60 flex w-full">
          {tabs.map((t) => (
            <button
              key={t.uid}
              onClick={() => setTab(t.uid)}
              data-id={`dev-tab-mobile-${t.uid}`} data-testid={`dev-tab-mobile-${t.uid}`}
              className={`flex items-center justify-center flex-1 min-w-0 py-3 text-[9px] tracking-[0.2em] uppercase ${tab === t.uid ? "text-softblack border-t-2 border-champagne" : "text-warmgray"}`}
            >
              <t.Icon size={16} strokeWidth={1.5} />
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-12">
        {tab === "logs" && <AdminEmailLogs />}
        {tab === "users" && <AdminUsers />}
        {tab === "email" && <AdminEmailSettings />}
        {tab === "settings" && <DeveloperSettings />}
        {tab === "profile" && <AdminProfile />}
      </main>
    </div>
  );
}

function DeveloperSettings() {
  const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN || "";
  const adminHeaders = { "Content-Type": "application/json", "X-Admin-Token": ADMIN_TOKEN };
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/admin/settings?order=setting_key ASC", { headers: adminHeaders })
      .then(r => r.json())
      .then(rows => {
        const emailRow = rows.find(r => r.setting_key === "email_enabled");
        if (emailRow) setEmailEnabled(emailRow.setting_value === "true" || emailRow.setting_value === "1");
      })
      .catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const rows = await fetch("/api/admin/settings?order=setting_key ASC", { headers: adminHeaders }).then(r => r.json());
      const existing = rows.find(r => r.setting_key === "email_enabled");
      if (existing) {
        await fetch("/api/admin/settings", {
          method: "PATCH",
          headers: adminHeaders,
          body: JSON.stringify({ uid: existing.uid, setting_value: String(emailEnabled) }),
        });
      } else {
        await fetch("/api/admin/settings", {
          method: "POST",
          headers: adminHeaders,
          body: JSON.stringify({ setting_key: "email_enabled", setting_value: String(emailEnabled) }),
        });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h2 className="font-serif text-3xl text-softblack">Configurações de Desenvolvimento</h2>
        <p className="text-warmgray text-sm mt-1">Configurações técnicas do sistema</p>
      </div>

      <div className="bg-white border border-fendi rounded-xl p-6 space-y-6">
        <h3 className="font-serif text-xl text-softblack">Sistema de Email</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-softblack font-medium">Enviar emails</p>
            <p className="text-warmgray text-xs mt-1">Ativa/desativa o envio de emails do sistema</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={emailEnabled} onChange={e => setEmailEnabled(e.target.checked)} className="sr-only peer" />
            <div className="w-11 h-6 bg-fendi/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-champagne"></div>
          </label>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary text-xs !py-2 !px-6">
          {saving ? "A guardar..." : "Guardar"}
        </button>
      </div>
    </div>
  );
}
